import { useState, useEffect } from 'react';

/**
 * Hook customizado para debounce de valores
 * Reduz requisições API esperando o usuário parar de digitar
 * 
 * @param value - Valor a ser debounced
 * @param delay - Delay em milissegundos (padrão: 500ms)
 * @returns Valor debounced
 * 
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebounce(searchTerm, 500);
 * 
 * useEffect(() => {
 *   // Esta requisição só dispara 500ms após o usuário parar de digitar
 *   fetchData(debouncedSearch);
 * }, [debouncedSearch]);
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Configurar timeout para atualizar o valor debounced
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Limpar timeout se o valor mudar (cleanup function)
    // Isso previne a atualização se o valor mudar antes do delay
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook alternativo que retorna valor e uma função de controle
 * Útil quando você precisa de mais controle sobre o debounce
 * 
 * @param initialValue - Valor inicial
 * @param delay - Delay em milissegundos (padrão: 500ms)
 * @returns [valor atual, valor debounced, setter]
 */
export function useDebouncedState<T>(
  initialValue: T,
  delay: number = 500
): [T, T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(initialValue);
  const debouncedValue = useDebounce(value, delay);

  return [value, debouncedValue, setValue];
}
